/**
 * Authentication API Service
 * Microservice-compatible authentication service using the auth provider pattern
 */

import { AUTH_API_CONFIG, AUTH_API_ENDPOINTS } from '../config/api.config';
import { createAuthProvider, type AuthProvider, type LoginCredentials, type LoginResponse, type RefreshTokenResponse } from './authProvider.service';
import { tokenService } from './token.service';
import { usesRefreshTokenApi, createProviderContext } from '../types/auth.types';

// Re-export types for backward compatibility
export interface LoginRequest extends LoginCredentials {
  email: string;
  password: string;
}

// Create auth provider instance
let authProvider: AuthProvider | null = null;

/**
 * Get or create the auth provider instance
 * Uses configuration from AUTH_API_CONFIG for microservice compatibility
 */
export function getAuthProvider(): AuthProvider {
  if (!authProvider) {
    authProvider = createAuthProvider({
      baseUrl: AUTH_API_CONFIG.baseUrl,
      loginEndpoint: AUTH_API_ENDPOINTS.login,
      refreshEndpoint: AUTH_API_ENDPOINTS.refresh,
      logoutEndpoint: AUTH_API_ENDPOINTS.logout,
      introspectEndpoint: AUTH_API_ENDPOINTS.introspect,
      timeout: AUTH_API_CONFIG.timeout,
    }, AUTH_API_CONFIG.providerType, tokenService);
  }
  return authProvider;
}

/**
 * Configure auth provider (useful for microservice environments)
 */
export function configureAuthProvider(provider: AuthProvider): void {
  authProvider = provider;
}

/**
 * Parse JWT token to extract payload (delegates to token service)
 */
export const parseJwt = (token: string) => {
  return tokenService.parseToken(token);
};

/**
 * Check if JWT token is expired (delegates to token service)
 */
export const isTokenExpired = (token: string): boolean => {
  return tokenService.isTokenExpired(token);
};

/**
 * Get token expiration time (delegates to token service)
 */
export const getTokenExpirationTime = (token: string): number | null => {
  return tokenService.getTokenExpirationTime(token);
};

/**
 * Login user with email and password
 */
export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const provider = getAuthProvider();
  return provider.login(credentials);
};

/**
 * Refresh access token using refresh token
 * 
 * HARD GUARD: This function should NEVER be called for MSAL provider.
 * MSAL uses acquireTokenSilent() instead of refreshTokenApi().
 * 
 * @throws Error if called for MSAL provider
 */
export const refreshToken = async (refreshTokenValue: string): Promise<RefreshTokenResponse> => {
  // HARD GUARD: Prevent refreshTokenApi() calls for MSAL provider
  const providerContext = createProviderContext(refreshTokenValue);
  
  if (!usesRefreshTokenApi(providerContext)) {
    throw new Error(
      '[authApi.service] CRITICAL: refreshTokenApi() called for MSAL provider. ' +
      'MSAL uses acquireTokenSilent(), not refreshTokenApi(). ' +
      'This is a programming error. Check call sites and use MSAL token acquisition instead.'
    );
  }
  
  const provider = getAuthProvider();
  return provider.refreshToken(refreshTokenValue);
};

/**
 * Logout user - Revoke token on backend only
 * Does NOT call provider.logout() to avoid triggering old logout flow (msalAuthProvider.logout).
 * The full logout orchestration (clear cache + logoutRedirect) is handled by LogoutService.
 */
export const logout = async (token: string): Promise<void> => {
  // For MSAL provider, only revoke token on backend - do NOT call provider.logout()
  // (provider.logout() contains old logout logic that conflicts with new LogoutService)
  if (AUTH_API_CONFIG.providerType === 'msal') {
    if (!AUTH_API_ENDPOINTS.logout) {
      return; // No backend logout endpoint configured
    }
    const logoutUrl = AUTH_API_ENDPOINTS.logout.startsWith('http')
      ? AUTH_API_ENDPOINTS.logout
      : `${AUTH_API_CONFIG.baseUrl}${AUTH_API_ENDPOINTS.logout}`;
    
    const axios = await import('axios').then(m => m.default);
    await axios.post(
      logoutUrl,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 5000,
      }
    );
    return;
  }
  
  // For non-MSAL providers, use the provider's logout method
  const provider = getAuthProvider();
  return provider.logout(token);
};

/**
 * Registration request interface
 */
export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  confirmPassword?: string;
  [key: string]: unknown;
}

/**
 * Registration response interface
 */
export interface RegisterResponse {
  success: boolean;
  message?: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * Register new user
 */
export const register = async (data: RegisterRequest): Promise<RegisterResponse> => {
  // In demo mode, simulate registration
  if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEMO_MODE === 'true') {
    await new Promise((resolve) => setTimeout(resolve, 800));
    return {
      success: true,
      message: 'Registration successful',
      user: {
        id: `user-${Date.now()}`,
        email: data.email,
        name: data.name,
      },
    };
  }

  // In production, call actual registration API
  const response = await fetch(`${AUTH_API_CONFIG.baseUrl}${AUTH_API_ENDPOINTS.register}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Registration failed' }));
    throw new Error(error.message || 'Registration failed');
  }

  return response.json();
};

/**
 * Introspect token (validate token with auth service)
 */
export const introspectToken = async (token: string) => {
  const provider = getAuthProvider();
  return provider.introspectToken(token);
};

